import { useRef, type JSX } from 'react';
import { useTranslation } from 'react-i18next';
import './ClueSlip.css';

interface ClueSlipProps {
  /**
   * The submitted clue text, or `null` for the unwritten state. When `null`,
   * the slip renders nothing — the cell is just the AvatarFrame. When a clue
   * arrives, the slip mounts beneath the frame, and the cell visibly grows.
   * That reflow IS the activity signal: the cell with the new clue extends
   * downward as the line inks in, registering motion in the player's
   * peripheral vision even if they weren't looking directly at that tile.
   */
  clue: string | null;
  /**
   * Owner's display name. Used in the screen-reader label so SR users hear
   * attribution as they tab through the cast grid.
   */
  ownerName: string;
  /**
   * When `true` at the moment this slip FIRST becomes visible (clue first
   * non-null), the entry ink-in keyframe is skipped — a cold arrival
   * (reconnect / boot-into-clue-giving) where the clue was already on the
   * server before we got here (handoff §6). Captured once at first-visible
   * render, so a slip that becomes visible LATER (a live submission) animates
   * normally even if the prop has since flipped. Default `false`.
   */
  suppressInk?: boolean;
  /**
   * Optional `data-testid` attached to the rendered slip. Set by E2E-targeted
   * callsites (discussion + clue-giving cast grids) so the reconnect spec can
   * assert visibility AND text content on the same DOM node. Wrapping the
   * caller in a span instead would add an empty flex-child for the null-clue
   * case and shift the cell's vertical rhythm by the parent's `gap` token.
   */
  testId?: string;
}

/**
 * Small printed caption that appears beneath an AvatarFrame *only* once
 * that player has submitted a clue. No empty / placeholder state — the
 * cell is silently the avatar until a clue lands, then the slip mounts and
 * grows the cell to its new height.
 *
 * Shared across clue-giving (where clues are submitted) and discussion +
 * the reveal arc (where the clues persist on the cast as the evidence the
 * room votes on and the Ghost deduces from).
 *
 * Cutive Mono per the Typed-Input Rule. No "composing" state — the active
 * speaker's slip stays absent during their turn; their input is at the
 * lectern, and the warm-shadow lift on their AvatarFrame plus the verso
 * prompt above carry the "they are writing" signal.
 */
export function ClueSlip({ clue, ownerName, suppressInk = false, testId }: ClueSlipProps): JSX.Element | null {
  const { t } = useTranslation();
  /* Capture the suppression decision the moment the clue first becomes
     visible (not at component mount — the slip instance exists while clue is
     null, and capturing then would wrongly freeze a player who later submits
     live). `null` = not yet captured. Once set, it is stable for the slip's
     lifetime, so a later prop flip never re-triggers the keyframe. */
  const suppressedRef = useRef<boolean | null>(null);
  if (clue !== null && suppressedRef.current === null) {
    suppressedRef.current = suppressInk;
  }
  if (clue === null) {
    /* No printed slip for the unwritten state — the absence IS the visual
       signal — but a screen reader can't perceive an absence, so an sr-only
       line carries it. Out-of-flow (position: absolute), so the cell's flex
       gap rhythm is untouched; no testId — the E2E specs read "slip present"
       as "clue submitted". */
    return <span className="susdle-sr-only">{t('cluegiving.cast.slip.empty')}</span>;
  }

  return (
    <span
      className="susdle-clue-slip"
      data-just-arrived={suppressedRef.current ? 'yes' : 'no'}
      data-testid={testId}
      aria-label={t('cluegiving.cast.slip.filled', { name: ownerName, clue })}
    >
      <span className="susdle-clue-slip__text">{clue}</span>
    </span>
  );
}
